import { faChevronDown, faChevronRight } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { styled, Typography } from '@mui/material';
import { useState } from 'react';

interface TreeViewProps {
  items: TreeItemProps[];
  isBold?: boolean;
}

export interface TreeItemProps {
  title: string;
  key: string;
  isBold?: boolean;
  children: TreeItemProps[];
}

export default function TreeView({ items, isBold, ...other }: TreeViewProps & React.ComponentProps<typeof Container>) {
  return (
    <Container {...other}>
      {items.map((item) => (
        <TreeItem key={item.key} item={{ ...item, isBold }} />
      ))}
    </Container>
  );
}

function TreeItem({
  item: { title, children, isBold },
  ...other
}: { item: TreeItemProps } & React.ComponentProps<typeof Node>) {
  const [minimised, setMinimised] = useState<boolean>(true);

  if (children.length > 0) {
    return (
      <Node {...other}>
        <button onClick={() => setMinimised((prev) => !prev)} type="button">
          <FixedWidthIcon size="xs" icon={minimised ? faChevronDown : faChevronRight} />
          <Typography fontWeight={isBold ? /* istanbul ignore next */ 'bold' : undefined}>{title}</Typography>
        </button>

        <SubContainer hidden={!minimised}>
          {children.map((item) => (
            <TreeItem item={item} key={item.key} />
          ))}
        </SubContainer>
      </Node>
    );
  }

  return (
    <PartNode>
      <Typography>{title}</Typography>
    </PartNode>
  );
}

const FixedWidthIcon = styled(FontAwesomeIcon)`
  width: 1.5em;
`;

const Container = styled('ul')`
  margin: 0;
  background-color: white;
  overflow: auto;
  flex: 1;
`;

const SubContainer = styled('ul')`
  padding-left: 1rem;
  margin: 0;
`;

const Node = styled('li')`
  list-style-type: none;

  button {
    display: flex;
    flex-direction: row;
    background-color: transparent;
    border: none;
    padding: 0;
    font: inherit;
    cursor: pointer;
    align-items: center;
    width: 100%;
  }

  button,
  p {
    border-radius: 0.3rem;
    padding: 0.2rem;
  }

  p:hover,
  button:hover {
    background-color: ${({ theme }) => theme.palette.grey['200']};
    transition: background-color 0.2s ease;
  }
`;

const PartNode = styled(Node)`
  list-style-type: initial;
  margin-left: 1.5rem;
`;
